import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { AiOutlineSearch } from "react-icons/ai";
import { BiSort } from "react-icons/bi";
import { FaChevronDown } from "react-icons/fa";

import PropertyComponent from "../../components/PropertyComponent";
import "./properties.css";
import config from "../../config";
import GeoSearch from "../../components/PropertyComponent/GeoSearch";
import { fetchFavoriteProperties } from "../../helpers/api";
import useGetProperties from "../../hooks/useGetProperties";
import { Button } from "reactstrap";
import FilterModal from "./FilterModal";
import { Container, Pagination } from "@mui/material";
import { searchAddresses } from "../../services/geocodingService";

import { deleteApi, getApi, postApi } from "../AppApis";
import LeafletMapComponent from "./LeafletMap";
import Balls from "../Templates/Loading";
import { GrMap } from "react-icons/gr";
import { FiFilter } from "react-icons/fi";
import ProfileDropdown from "../../components/ProfileDropdown";
import MobileHeader from "../../components/MobileHeader";
import { showErrorToast, showSuccessToast } from "../utils";
import PreviewPage from "../Preview/Preview";
import { toast } from "react-toastify";
import PropertySkeleton from "./PropertySkeleton";

const sortOptions = [
    { value: "newest", label: "Newest" },
    { value: "oldest", label: "Oldest" },
    { value: "low_to_high", label: "Price (Low to High)" },
    { value: "high_to_low", label: "Price (High to Low)" },
];
const Properties = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [propertiesCount, setPropertiesCount] = useState(0); // Add this line
    const [properties, setProperties] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [previewModal, setPreviewModal] = useState(null);
    const [loading, setLoading] = useState(false);
    const [loadProperties, setLoadProperties] = useState(false);
    const [loadingFilters, setLoadingFilters] = useState(false);
    const [cornersPayload, setCornersPayload] = useState(null);
    const [polygenPayload, setPolygenPayload] = useState(null);

    const [filteredProperties, setFilteredProperties] = useState([]);
    const [locationArrayFinal, setLocaionArrayFinal] = useState([]);
    const [center, setCenter] = useState({ lat: 44.986656, lng: -93.258133 });
    const { loadingProperties } = useGetProperties();
    const [showMap, setShowMap] = useState(true);
    const [mapBounds, setMapBounds] = useState(null);
    const [selectedMarker, setSelectedMarker] = useState(null);
    const [favoriteProperties, setFavoriteProperties] = useState([]);
    const [isShareModal, setIsShareModal] = useState(false);
    const minneapolisCenter = { lat: 40.9778, lng: -93.265 };
    const defaultZoomLevel = 16;
    const [savedSearches, setSavedSearches] = useState([]);
    const [isSmallScreen, setIsSmallScreen] = useState(false);
    const [toggle, setToggle] = useState(false);
    const [onBoundZoom, setOnBoundZoom] = useState(16);
    const [selectedOption, setSelectedOption] = useState(sortOptions[0]);
    const [isOpen, setIsOpen] = useState(false);
    const [isFullViewMap, setIsFullViewMap] = useState(false);
    const [isFavoriteChanged, setIsFavoriteChanged] = useState(0);
    const mapRef = useRef(null);
    const propertyViewRef = useRef(null);
    const [searchedArea, setSearchedArea] = useState(null);
    const [zipCode, setZipCode] = useState(null);
    const [filters, setFilters] = useState({
        forSale: "",
        beds: "",
        baths: "",
        homeType: "",
        sort: "newest",
    });
    const handleResize = () => {
        if (window.innerWidth < 749) {
            setIsSmallScreen(true);
            // On mobile, ensure full view mode is disabled to prevent header/search bar from disappearing
            if (isFullViewMap) {
                setIsFullViewMap(false);
            }
        } else {
            setIsSmallScreen(false);
        }
    };

    useEffect(() => {
        handleResize();

        window.addEventListener("resize", handleResize);

        // Additional mobile header visibility handling
        const handleScroll = () => {
            if (window.innerWidth <= 768) {
                // Force scroll to top to keep header visible
                if (window.scrollY > 0) {
                    window.scrollTo(0, 0);
                }
            }
        };

        const handleTouchEnd = () => {
            if (window.innerWidth <= 768) {
                // Force header to stay visible after touch interactions
                setTimeout(() => {
                    window.scrollTo(0, 0);
                    document.body.scrollTop = 0;
                    document.documentElement.scrollTop = 0;
                }, 100);
            }
        };

        if (window.innerWidth <= 768) {
            window.addEventListener("scroll", handleScroll, { passive: true });
            document.addEventListener("touchend", handleTouchEnd, { passive: true });
        }

        return () => {
            window.removeEventListener("resize", handleResize);
            window.removeEventListener("scroll", handleScroll);
            document.removeEventListener("touchend", handleTouchEnd);
        };
    }, [isFullViewMap]);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

    useEffect(() => {
        getCornersProperties();
    }, [filters]);

    // Handle pagination when no map bounds are set
    useEffect(() => {
        if (!cornersPayload && !polygenPayload) {
            getCornersProperties();
        }
    }, [currentPage, filters.sort]);

    const areFiltersApplied = () => {
        return (
            filters.minPrice ||
            filters.maxPrice ||
            filters.beds ||
            filters.baths ||
            (Array.isArray(filters.homeType) && filters.homeType.length > 0) ||
            filters.forSale
        );
    };

    const clearFilters = () => {
        setFilters({
            forSale: "",
            price: { min: "", max: "" },
            beds: "",
            baths: "",
            homeType: "",
            sort: "newest",
        });
    };
    const filterPropertiesByMapBounds = () => {
        // Only filter by map bounds if NO polygon is drawn
        if (mapBounds && !polygenPayload) {
            const propertiesInBounds = properties.filter((property) => {
                return (
                    property.latitude >= mapBounds.south &&
                    property.latitude <= mapBounds.north &&
                    property.longitude >= mapBounds.west &&
                    property.longitude <= mapBounds.east
                );
            });

            // Set filtered properties or fallback to all properties
            setFilteredProperties(propertiesInBounds.length > 0 ? propertiesInBounds : properties);
        }
    };

    useEffect(() => {
        // Only run map bounds filtering if no polygon is active
        if (!polygenPayload) {
            filterPropertiesByMapBounds();
        }
    }, [mapBounds, properties, polygenPayload]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            // Check if the clicked element is outside the InfoWindow (for Leaflet popups)
            if (selectedMarker && mapRef.current) {
                const popupDom = document.querySelector(".leaflet-popup"); // Leaflet popup class
                if (popupDom && !popupDom.contains(event.target)) {
                    setSelectedMarker(null); // Close the popup
                }
            }
        };

        // Add the event listener to the document for Leaflet
        if (mapRef.current) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [selectedMarker]);

    const getCornersProperties = async (corners, address) => {
        try {
            setLoadProperties(true);
            const filterPayload = {};
            let payload = { maxPrice: "" };

            if (filters.beds !== "") {
                filterPayload.beds = filters.beds;
            }
            if (filters.baths !== "") {
                filterPayload.baths = filters.baths;
            }
            if (filters?.homeType !== "") {
                filterPayload.type = filters.homeType;
            }
            if (filters.maxPrice !== "") {
                filterPayload.maxPrice = filters.maxPrice;
            }
            if (filters.minPrice) {
                filterPayload.minPrice = filters.minPrice;
            }
            if (zipCode) {
                filterPayload.PostalCode = zipCode;
            }
            let url = "/properties/all_properties?page=" + currentPage;
            if (filters.sort) {
                url = url + "&sortby=" + filters.sort;
            }

            if (filterPayload && !polygenPayload) {
                payload = {
                    ...cornersPayload,
                    ...filterPayload,
                };
            }
            if (filterPayload && polygenPayload) {
                payload = {
                    ...polygenPayload,
                    ...filterPayload,
                };
            }
            if (!address && !filterPayload && !polygenPayload) {
                payload = {
                    ...cornersPayload,
                };
            }
            if (!address && !filterPayload && polygenPayload) {
                payload = {
                    ...polygenPayload,
                };
            }

            if (address) {
                payload = { ...address };
            }
            const response = await postApi(url, payload);
            if (response?.error === 401) {
                navigate("/login");
                return;
            }

            if (response?.response?.items) {
                const propertiesLocal = response.response.items;

                let updatedProperties = propertiesLocal.map((property) => ({
                    ...property,
                    isFavorite: property.is_favorited,
                    // Normalize price field: Map ListPrice to price for PropertyComponent compatibility
                    price: property.ListPrice || property.price,
                }));

                // Apply client-side sorting fix for price-based sorting
                // This ensures properties with invalid prices (0, null, undefined, or unrealistically low) are placed at the end
                if (filters.sort === "low_to_high" || filters.sort === "high_to_low") {
                    const validProperties = [];
                    const invalidProperties = [];

                    updatedProperties.forEach((property) => {
                        const price = parseFloat(property.price);
                        // Consider properties with price <= 1 or invalid prices as invalid
                        if (isNaN(price) || price === null || price === undefined || price <= 1) {
                            invalidProperties.push(property);
                        } else {
                            validProperties.push(property);
                        }
                    });

                    // Sort valid properties numerically
                    validProperties.sort((a, b) => {
                        const priceA = parseFloat(a.price);
                        const priceB = parseFloat(b.price);

                        if (filters.sort === "low_to_high") {
                            return priceA - priceB;
                        } else {
                            return priceB - priceA;
                        }
                    });

                    // Combine: valid properties first, then invalid ones at the end
                    updatedProperties = [...validProperties, ...invalidProperties];
                }

                setProperties(updatedProperties);

                setFilteredProperties(updatedProperties); // Ensure filtered properties are set
                setPropertiesCount(response.response.total);
                setCurrentPage(response.response.page);
                // Use totalPages from API response, or calculate based on total items and items per page
                setTotalPages(response.response.totalPages || Math.ceil(response.response.total / 10));
                if (propertyViewRef.current) {
                    propertyViewRef.current.scrollIntoView({ behavior: "smooth" });
                }
            } else {
                setProperties([]);
                setPropertiesCount(0);
                setFilteredProperties([]); // Ensure filtered properties are set
            }
            setLoadProperties(false);
        } catch (error) {
            console.log(error);
        }
    };

    useEffect(() => {
        if (cornersPayload || polygenPayload) {
            getCornersProperties();
        }
    }, [currentPage, filters.sort, cornersPayload, polygenPayload]);

    useEffect(() => {
        if (isFavoriteChanged > 0) {
            getCornersProperties();
        }
    }, [isFavoriteChanged]);

    const [searchLocation, setSearchLocation] = useState("");
    const [searchCoordinates, setSearchCoordinates] = useState(null);
    const [resultSearchLocation, setResultSearchLocation] = useState("");

    const onBoundingBoxUpdate = (box, place) => {
        setSearchedArea(box);
        if (!mapRef.current) {
            console.error("Map instance is not available");
            return;
        }

        if (!place || !place.geometry || !place.geometry.location) {
            console.error("Invalid place data:", place); // Log if place is invalid
            return;
        }
        setSearchLocation(place.formatted_address);
        setResultSearchLocation(place.formatted_address);
        setSearchCoordinates(place.geometry.location.toJSON());

        let calculatedZoom = 2;

        // Set zoom level based on location type
        if (place.types.includes("country")) {
            calculatedZoom = 4;
        } else if (place.types.includes("administrative_area_level_1")) {
            calculatedZoom = 7;
        } else if (place.types.includes("locality")) {
            calculatedZoom = 10;
        } else if (place.types.includes("street_address") || place.types.includes("premise")) {
            calculatedZoom = 15;
        } else if (place.types.includes("postal_code")) {
            calculatedZoom = 12;
        } else if (place.types.includes("subpremise")) {
            calculatedZoom = 16;
        }

        setOnBoundZoom(calculatedZoom);

        if (mapRef.current) {
            // Use Leaflet's setView method which combines panTo and setZoom
            const coords = place.geometry.location.toJSON();
            mapRef.current.setView([coords.lat, coords.lng], calculatedZoom);
        }
    };

    const deleteSearchLocation = (indexToBeDeleted) => {
        let updatedLocationArray = [...locationArrayFinal];

        updatedLocationArray.splice(indexToBeDeleted, 1);

        setLocaionArrayFinal(updatedLocationArray);

        if (updatedLocationArray.length === 0) {
            // Reset all relevant states to default values
            setMapBounds(null); // Clear any existing bounds
            setSearchLocation(""); // Clear the search input value
            setSearchCoordinates(null); // Clear any coordinates
            setFilteredProperties([]); // Clear filtered properties if needed

            // Reset the map center and zoom to Minneapolis
            setCenter(minneapolisCenter);

            if (mapRef.current) {
                mapRef.current.setView([minneapolisCenter.lat, minneapolisCenter.lng], defaultZoomLevel);
            }
        } else {
        }
    };

    const clearSearchLocation = () => {
        setSearchLocation("");
        setResultSearchLocation("");
        setSearchCoordinates(null);
        setMapBounds(null);
        setFilteredProperties([]);
        setZipCode("");
        // Clear polygon and corner payloads to remove drawn boundaries and hide "Clear boundary" button
        setPolygenPayload(null);
        setCornersPayload(null);
        setSearchedArea(null);

        // Reset to show all properties without any location filters
        getCornersProperties();
    };

    useEffect(() => {
        document.title = `Properties | ${config.get("COMPANY_NAME")}`;
    }, []);

    const handleFilterChange = (updatedFilter, updatedKey) => {
        setFilters((prevFilters) => {
            const newFilters = {
                ...prevFilters,
                [updatedKey.name]: updatedFilter.value,
            };
            return newFilters;
        });
    };

    const handleSearchLocation = async () => {
        if (!searchLocation) return;

        try {
            setLoading(true);

            // Use the new geocoding service instead of direct Nominatim call
            const results = await searchAddresses(searchLocation, 1);

            if (results && results.length > 0) {
                const location = { lat: results[0].lat, lng: results[0].lng };
                setSearchCoordinates(location);
                filterPropertiesByLocation(location);
            } else {
                console.error("Address search was not successful");
                showErrorToast("Location not found. Please try a different address.", {
                    autoClose: 3000,
                    pauseOnHover: false,
                });
            }
        } catch (error) {
            console.error("Error searching location:", error);
            showErrorToast("Error searching location. Please try again.", {
                autoClose: 3000,
                pauseOnHover: false,
            });
        } finally {
            setLoading(false);
        }
    };

    const filterPropertiesByLocation = (location) => {
        const radius = 5000;
        const filtered = properties.filter((property) => {
            const distance = getDistanceFromLatLonInKm(location.lat, location.lng, property.latitude, property.longitude);
            return distance <= radius;
        });
        setFilteredProperties(filtered);
    };

    const getDistanceFromLatLonInKm = (lat1, lon1, lat2, lon2) => {
        const R = 6371;
        const dLat = deg2rad(lat2 - lat1);
        const dLon = deg2rad(lon2 - lon1);
        const a =
            Math.sin(dLat / 2) * Math.sin(dLat / 2) + Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        const distance = R * c;
        return distance;
    };

    const deg2rad = (deg) => {
        return deg * (Math.PI / 180);
    };

    const renderImages = (images) => {
        const fallbackImage = "/path/to/fallback-image.jpg"; // Set a valid placeholder image path

        return images.map((url, index) => (
            <img
                key={index}
                src={url}
                alt={`Property Image ${index + 1}`}
                onError={(e) => {
                    console.error(`Failed to load image: ${url}`);
                    e.target.src = fallbackImage; // Replace the failed image with the fallback
                }}
                style={{ maxWidth: "100%", height: "auto", marginBottom: "10px" }}
            />
        ));
    };

    useEffect(() => {
        getFavoritePropertyIds();
    }, []);

    const getFavoritePropertyIds = async () => {
        const authUserToken = localStorage.getItem("authUser");
        const authUser = authUserToken ? authUserToken.replace(/^"|"$/g, "") : "";
        try {
            // const favoritePropertyIds = await fetchFavoritePropertyIds(authUser);
            const favoriteProperty = await fetchFavoriteProperties(authUser);
            const favoritePropertyIds = favoriteProperty && favoriteProperty?.map((fav) => fav.ListingId);
            setFavoriteProperties(favoritePropertyIds);
        } catch (error) {
            // console.log("error in favorite properties ids fetching: ", error);
        }
    };

    const isFavorite = (propertyId) => {
        return propertyId.isFavorite;
    };

    const handleFavorite = async (propertyId) => {
        const checkFavorite = isFavorite(propertyId);
        const path = "/property-services/favorite_property";
        let response;
        const data = { ...propertyId };
        if (checkFavorite) {
            response = await deleteApi(path, { listing_id: data.ListingId });
            const copyProperties = JSON.parse(JSON.stringify(properties));
            const filterFav = copyProperties.map((item) => {
                if (item.ListingId === propertyId.ListingId) {
                    return { ...item, isFavorite: false };
                }
                return item;
            });
            setProperties(filterFav);
            setFilteredProperties((prevFiltered) =>
                prevFiltered.map((item) => (item.ListingId === propertyId.ListingId ? { ...item, isFavorite: false } : item))
            );
            toast.success("Property removed from favorites", { toastId: "remove-fav" });
        } else {
            response = await postApi(path, data);
            if (response.error === 401) {
                navigate("/login");
                return;
            }
            //deep copy
            const copyProperties = JSON.parse(JSON.stringify(properties));
            const filterFav = copyProperties.map((item) => {
                if (item.ListingId === propertyId.ListingId) {
                    return { ...item, isFavorite: true };
                }
                return item;
            });
            setProperties(filterFav);
            setFilteredProperties((prevFiltered) =>
                prevFiltered.map((item) => (item.ListingId === propertyId.ListingId ? { ...item, isFavorite: true } : item))
            );
            toast.success("Property added to favorites", { toastId: "add-fav" });
        }
        setIsFavoriteChanged(isFavoriteChanged + 1);
    };

    const handleSaveSearch = async () => {
        if (!searchLocation || !searchCoordinates) {
            console.error("No search location or coordinates available to save.");
            showErrorToast("Please enter a valid location to save the search.", {
                autoClose: 3000,
                pauseOnHover: false,
            });
            return;
        }

        const { lat, lng } = searchCoordinates;
        const searchData = {
            description: searchLocation,
            longitude: lng.toString(),
            latitude: lat.toString(),
        };

        try {
            const response = await postApi("/notifications/saved_searches", searchData);
            if (response?.error === 401) {
                navigate("/login");
                return;
            }

            if (response.response) {
                const result = response.response;
                console.log("Search saved successfully:", result);
                showSuccessToast("Search saved successfully!", {
                    autoClose: 3000,
                    pauseOnHover: false,
                });
                setSavedSearches((prevSavedSearches) => [...prevSavedSearches, { location: searchLocation }]);
            } else {
                console.error("Failed to save search:", response);
                showErrorToast("Failed to save search. Please try again.", {
                    autoClose: 3000,
                    pauseOnHover: false,
                });
            }
        } catch (error) {
            console.error("Error saving search:", error);
            showErrorToast("An error occurred while saving the search. Please try again.", {
                autoClose: 3000,
                pauseOnHover: false,
            });
        }
    };

    const fetchSavedSearches = async () => {
        const authUserToken = localStorage.getItem("authUser");
        const authUser = authUserToken ? authUserToken.replace(/^"|"$/g, "") : null;

        if (!authUser) {
            console.error("No authentication token found.");
            return;
        }

        try {
            const response = await getApi("/notifications/saved_searches");

            if (response.response) {
                const data = response?.response?.data;

                // Use `_id` directly to set saved searches
                const formattedSearches = data?.map((search) => ({
                    _id: search._id,
                    description: search.description,
                    latitude: search.latitude,
                    longitude: search.longitude,
                }));

                setSavedSearches(formattedSearches);
            } else {
                console.log("Failed to fetch saved searches:", response.statusText);
            }
        } catch (error) {}
    };

    useEffect(() => {
        fetchSavedSearches();
    }, []);

    // Handle incoming search state from saved searches
    useEffect(() => {
        const state = location.state;
        if (state && state.applySearch && state.searchLocation) {
            setSearchLocation(state.searchLocation);
            setCenter({ lat: state.latitude, lng: state.longitude });

            // Trigger search automatically
            setTimeout(() => {
                handleSearchLocation();
            }, 500);

            // Clear the state to prevent re-triggering
            navigate("/properties", { replace: true });
        }
    }, [location.state]);

    // This useEffect is now redundant as the filtering logic has been moved to filterPropertiesByMapBounds()
    // Keeping this comment for reference - the functionality is handled at lines 179-184

    const [isModalOpen, setIsModalOpen] = useState(false);

    const toggleModal = () => setIsModalOpen(!isModalOpen);

    return (
        <>
            <Container
                onClick={() => (isModalOpen ? setIsModalOpen(false) : {})}
                className="tw-bg-background_color tw-p-0 tw-h-screen tw-flex tw-flex-col tw-justify-between tw-overflow-hidden"
                maxWidth=""
            >
                {loadingProperties ? (
                    <div>
                        {/* Skeleton for header */}
                        <div className="tw-filter-wrapper tw-w-full tw-flex tw-flex-nowrap tw-items-center tw-justify-between tw-gap-1 tw-bg-tertiary tw-h-[55px]">
                            <div className="md:tw-flex tw-hidden tw-h-[20px] tw-w-[20px]" />
                            <div className="max-sm:tw-hidden max-sm:tw-w-[85%] max-md:tw-w-[90%] tw-gap-2 tw-flex tw-justify-center tw-items-center">
                                <div className="tw-h-[40px] tw-bg-gray-200 tw-rounded-lg tw-animate-pulse tw-flex-grow"></div>
                                <div className="tw-h-[40px] tw-w-24 tw-bg-gray-200 tw-rounded-lg tw-animate-pulse max-sm:tw-hidden"></div>
                            </div>
                            <div className="tw-flex tw-gap-2 tw-items-center">
                                <div className="tw-h-8 tw-w-8 tw-bg-gray-200 tw-rounded-full tw-animate-pulse"></div>
                                <div className="tw-h-4 tw-w-20 tw-bg-gray-200 tw-rounded tw-animate-pulse md:tw-hidden"></div>
                            </div>
                        </div>

                        <div style={{ height: "calc(100vh - 55px)" }} className="tw-flex tw-flex-col">
                            <div style={{ height: "calc(100vh - 55px)" }}>
                                <div className="tw-flex tw-h-full tw-border-2 max-md:tw-flex-col max-sm:tw-flex-col">
                                    {/* Map skeleton */}
                                    <div className="tw-relative tw-h-full max-md:tw-h-[40vh] tw-w-full xl:tw-w-full">
                                        <div className="tw-w-full tw-h-full tw-bg-gray-200 tw-animate-pulse"></div>
                                    </div>

                                    {/* Properties list skeleton */}
                                    <div className="tw-z-[5] tw-w-[70%] 2xl:tw-w-[60%] tw-h-full tw-overflow-hidden tw-overflow-y-scroll tw-scrollbar-hide max-md:tw-max-h-[60vh] tw-bg-white max-md:tw-w-full max-sm:tw-w-full tw-p-4 max-sm:tw-max-h-[100vh] tw-px-2">
                                        {/* Filter controls skeleton */}
                                        <div className="tw-w-full tw-flex-wrap tw-flex tw-items-center tw-justify-end tw-h-fit tw-gap-[7px] tw-mb-4">
                                            <div className="tw-h-[40px] tw-w-20 tw-bg-gray-200 tw-rounded-lg tw-animate-pulse"></div>
                                            <div className="tw-h-[40px] tw-w-32 tw-bg-gray-200 tw-rounded-lg tw-animate-pulse"></div>
                                        </div>

                                        {/* Location and results skeleton */}
                                        <div className="tw-mt-4 tw-mb-3 tw-w-full 2xl:tw-px-5">
                                            <div className="tw-h-6 tw-bg-gray-200 tw-rounded tw-w-48 tw-mb-2 tw-animate-pulse"></div>
                                            <div className="tw-h-4 tw-bg-gray-200 tw-rounded tw-w-24 tw-animate-pulse"></div>
                                        </div>

                                        {/* Property grid skeleton */}
                                        <div className="tw-grid tw-gap-[10px] tw-scrollbar-hide tw-place-items-center tw-grid-cols-2 min-lg:tw-grid-cols-1 max-sm:tw-grid-cols-1 max-md:tw-grid-cols-2 lg:tw-grid-cols-2">
                                            {[...Array(6)].map((_, index) => (
                                                <PropertySkeleton key={index} />
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div>
                        {/* Mobile Header - Only visible on mobile */}
                        <MobileHeader screenName="Find Properties" />

                        {/* Desktop Header - Hidden on mobile */}
                        <div
                            className={`tw-filter-wrapper tw-w-full tw-hidden md:tw-flex tw-flex-nowrap tw-items-center tw-justify-between tw-gap-1 tw-bg-tertiary tw-h-[55px] ${previewModal ? "tw-hidden md:tw-flex" : ""}`}
                        >
                            <div className="md:tw-flex tw-hidden tw-h-[20px] tw-w-[20px]" />
                            <div className="tw-gap-2 tw-flex tw-justify-center tw-items-center">
                                <div className="tw-filter-search-wrapper tw-h-[40px] tw-select-filter tw-bg-white tw-text-sm tw-text-gray-700 tw-flex-grow tw-border tw-border-[#D0D5DD] tw-rounded-lg tw-overflow-hidden">
                                    <GeoSearch
                                        onBoundingBoxUpdate={onBoundingBoxUpdate}
                                        deleteSearchLocation={deleteSearchLocation}
                                        setAddress={setSearchLocation}
                                        searchLocation={resultSearchLocation}
                                        setZipCode={setZipCode}
                                        clearSearchLocation={clearSearchLocation}
                                        className="search-box-filter"
                                    />
                                    <button className="search-btn-filter" style={{ background: '#208177' }} onClick={handleSearchLocation}>
                                        <AiOutlineSearch />
                                    </button>
                                </div>
                                <button
                                    className="tw-save-search tw-rounded-lg tw-h-[40px] topbarFilters tw-flex tw-justify-center tw-text-center"
                                    onClick={() => {
                                        handleSaveSearch();
                                    }}
                                >
                                    Save Search
                                </button>
                            </div>
                            <div className="tw-flex tw-gap-2 tw-items-center">
                                <div className="w-[20%] max-md:tw-w-[10%]">
                                    <ProfileDropdown />
                                </div>
                            </div>
                        </div>
                        <div
                            className={`${isSmallScreen && toggle && "tw-hidden"} md:tw-hidden tw-h-auto tw-min-h-[55px] tw-p-2 tw-bg-background_color tw-rounded-lg tw-text-sm tw-text-gray-700 tw-flex-grow tw-flex tw-flex-wrap tw-items-center tw-gap-2`}
                        >
                            <div className="tw-flex tw-items-center tw-flex-1 tw-bg-white tw-rounded-full tw-border tw-border-[#E0DEF7] tw-h-[40px] tw-px-2 tw-shadow-sm">
                                <GeoSearch
                                    onBoundingBoxUpdate={onBoundingBoxUpdate}
                                    deleteSearchLocation={deleteSearchLocation}
                                    searchLocation={resultSearchLocation}
                                    clearSearchLocation={clearSearchLocation}
                                    className="tw-flex-1 tw-bg-transparent tw-outline-none tw-text-black tw-text-[14px] tw-pl-2"
                                />
                                <button
                                    className="tw-bg-[#208177] tw-rounded-full tw-w-[32px] tw-h-[32px] tw-flex tw-items-center tw-justify-center tw-ml-1"
                                    onClick={handleSearchLocation}
                                >
                                    <AiOutlineSearch className="tw-text-white tw-text-[16px]" />
                                </button>
                            </div>
                            <button
                                className="tw-bg-[#208177] tw-rounded-lg tw-h-[30px] tw-w-[100px] tw-flex tw-items-center tw-justify-center tw-text-white tw-text-[12px] tw-font-medium"
                                onClick={() => {
                                    handleSaveSearch();
                                }}
                            >
                                Saved Search
                            </button>
                        </div>
                        <div style={{ height: "calc(100vh - 55px)" }} className=" tw-flex tw-flex-col  ">
                            <div style={{ height: "calc(100vh - 55px)" }}>
                                {loadingFilters ? (
                                    <div className="tw-flex tw-h-full tw-border-2 max-md:tw-flex-col max-sm:tw-flex-col">
                                        {/* Map skeleton */}
                                        <div className="tw-relative tw-h-full max-md:tw-h-[40vh] tw-w-full xl:tw-w-full">
                                            <div className="tw-w-full tw-h-full tw-bg-gray-200 tw-animate-pulse"></div>
                                        </div>

                                        {/* Properties list skeleton */}
                                        <div className="tw-z-[5] tw-w-[70%] 2xl:tw-w-[60%] tw-h-full tw-overflow-hidden tw-overflow-y-scroll tw-scrollbar-hide max-md:tw-max-h-[60vh] tw-bg-white max-md:tw-w-full max-sm:tw-w-full tw-p-4 max-sm:tw-max-h-[100vh] tw-px-2">
                                            {/* Filter controls skeleton */}
                                            <div className="tw-w-full tw-flex-wrap tw-flex tw-items-center tw-justify-end tw-h-fit tw-gap-[7px] tw-mb-4">
                                                <div className="tw-h-[40px] tw-w-20 tw-bg-gray-200 tw-rounded-lg tw-animate-pulse"></div>
                                                <div className="tw-h-[40px] tw-w-32 tw-bg-gray-200 tw-rounded-lg tw-animate-pulse"></div>
                                            </div>

                                            {/* Location and results skeleton */}
                                            <div className="tw-mt-4 tw-mb-3 tw-w-full 2xl:tw-px-5">
                                                <div className="tw-h-6 tw-bg-gray-200 tw-rounded tw-w-48 tw-mb-2 tw-animate-pulse"></div>
                                                <div className="tw-h-4 tw-bg-gray-200 tw-rounded tw-w-24 tw-animate-pulse"></div>
                                            </div>

                                            {/* Property grid skeleton */}
                                            <div className="tw-grid tw-gap-[10px] tw-scrollbar-hide tw-place-items-center tw-grid-cols-2 min-lg:tw-grid-cols-1 max-sm:tw-grid-cols-1 max-md:tw-grid-cols-2 lg:tw-grid-cols-2">
                                                {[...Array(4)].map((_, index) => (
                                                    <PropertySkeleton key={index} />
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <div className={`tw-flex tw-h-full tw-border-2   max-md:tw-flex-col  max-sm:tw-flex-col `}>
                                        {showMap && (
                                            <div
                                                className={`tw-relative tw-h-full max-md:tw-h-[40vh] ${isSmallScreen && toggle && "tw-hidden"} ${isFullViewMap ? "tw-w-full tw-h-full" : "tw-w-full xl:tw-w-full"} `}
                                            >
                                                <div className=" tw-relative  tw-w-full tw-h-full ">
                                                    <LeafletMapComponent
                                                        setPreviewModal={setPreviewModal}
                                                        mapRef={mapRef}
                                                        searchedArea={searchedArea}
                                                        setSearchedArea={setSearchedArea}
                                                        filters={filters}
                                                        setCurrentPage={setCurrentPage}
                                                        setAddress={setSearchLocation}
                                                        onBoundZoom={onBoundZoom}
                                                        setCornersPayload={setCornersPayload}
                                                        visibleRegionPayload={cornersPayload}
                                                        setPolygenPayload={setPolygenPayload}
                                                        setPropertiesCount={setPropertiesCount}
                                                        isFullViewMap={isFullViewMap}
                                                        setIsFullViewMap={setIsFullViewMap}
                                                        isFilterModalOpen={isModalOpen}
                                                    />
                                                </div>
                                            </div>
                                        )}

                                        <div
                                            ref={propertyViewRef}
                                            style={{ boxShadow: "-6px 9px 14px 0px rgba(0, 0, 0, 0.25)" }}
                                            className={`tw-z-[5] tw-w-full md:tw-w-[70%] 2xl:tw-w-[60%] tw-h-full tw-overflow-hidden ${isSmallScreen && !toggle ? "tw-hidden" : ""}
                                            ${isFullViewMap ? "tw-hidden" : "tw-block"}
                                            ${showMap ? "tw-overflow-y-scroll tw-scrollbar-hide" : ""}
                                            max-md:tw-max-h-[60vh] tw-bg-white tw-p-2 sm:tw-p-4 max-sm:tw-max-h-[100vh]`}
                                        >
                                            {/* Mobile search bar, filters, and saved search - visible only on mobile in list view */}
                                            <div className="sm:tw-hidden tw-flex tw-flex-col tw-gap-2 tw-mb-3">
                                                <div className="tw-flex tw-items-center tw-gap-2">
                                                    <div className="tw-flex tw-items-center tw-flex-1 tw-bg-white tw-rounded-full tw-border tw-border-[#E0DEF7] tw-h-[40px] tw-px-2 tw-shadow-sm">
                                                        <GeoSearch
                                                            onBoundingBoxUpdate={onBoundingBoxUpdate}
                                                            deleteSearchLocation={deleteSearchLocation}
                                                            searchLocation={resultSearchLocation}
                                                            clearSearchLocation={clearSearchLocation}
                                                            className="tw-flex-1 tw-bg-transparent tw-outline-none tw-text-black tw-text-[14px] tw-pl-2"
                                                        />
                                                        <button
                                                            className="tw-bg-[#208177] tw-rounded-full tw-w-[32px] tw-h-[32px] tw-flex tw-items-center tw-justify-center tw-ml-1"
                                                            onClick={handleSearchLocation}
                                                        >
                                                            <AiOutlineSearch className="tw-text-white tw-text-[16px]" />
                                                        </button>
                                                    </div>
                                                </div>
                                                <div className="tw-flex tw-items-center tw-gap-2">
                                                    <button
                                                        className="tw-bg-white tw-border tw-border-[#D0D5DD] tw-rounded-lg tw-h-[30px] tw-w-[100px] tw-flex tw-items-center tw-justify-center tw-gap-1 tw-text-[12px] tw-font-medium tw-text-[#171717]"
                                                        onClick={toggleModal}
                                                    >
                                                        <FiFilter className="tw-text-[14px]" />
                                                        Filters
                                                    </button>
                                                    <div className="tw-relative inline-block text-left tw-flex-1">
                                                        <button
                                                            onClick={() => setIsOpen(!isOpen)}
                                                            className="tw-bg-white tw-border tw-text-[11px] tw-h-[30px] tw-text-nowrap tw-border-[#D0D5DD] tw-rounded-lg tw-text-[#171717] tw-flex tw-items-center tw-justify-center tw-gap-1 tw-leading-none tw-px-2 tw-w-full"
                                                        >
                                                            <BiSort className="tw-text-[14px]" />
                                                            <span className="tw-truncate">{selectedOption.label}</span>
                                                            <FaChevronDown className="tw-text-xs tw-ml-1 tw-flex-shrink-0" />
                                                        </button>

                                                        {isOpen && (
                                                            <div className="tw-absolute tw-mt-2 tw-z-[90] tw-w-full tw-min-w-[140px] tw-bg-white tw-border tw-border-gray-300 tw-rounded-lg tw-shadow-md tw-left-0">
                                                                {sortOptions.map((option) => (
                                                                    <button
                                                                        key={option.value}
                                                                        onClick={() => {
                                                                            setSelectedOption(option);
                                                                            setCurrentPage(1);
                                                                            handleFilterChange({ value: option.value }, { name: "sort" });
                                                                            setIsOpen(false);
                                                                        }}
                                                                        className="tw-flex tw-gap-2 tw-w-full tw-py-2 tw-text-left tw-px-3 hover:tw-bg-gray-100 tw-text-[12px] tw-whitespace-nowrap"
                                                                    >
                                                                        {option.label}
                                                                    </button>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                    <button
                                                        className="tw-bg-[#208177] tw-rounded-lg tw-h-[30px] tw-w-[100px] tw-flex tw-items-center tw-justify-center tw-text-white tw-text-[12px] tw-font-medium"
                                                        onClick={() => {
                                                            handleSaveSearch();
                                                        }}
                                                    >
                                                        Saved Search
                                                    </button>
                                                </div>
                                            </div>

                                            {/* Desktop filter controls */}
                                            <div className="tw-w-full tw-hidden sm:tw-flex tw-flex-wrap tw-items-center tw-justify-center sm:tw-justify-end tw-h-fit tw-gap-2 sm:tw-gap-[7px]">
                                                <button
                                                    onClick={toggleModal}
                                                    className="tw-py-[8px] tw-relative tw-px-[12px] sm:tw-px-[15px] tw-text-[12px] sm:tw-text-[14px] tw-leading-none tw-h-[36px] sm:tw-h-[40px] tw-items-center 2xl:tw-text-xl tw-flex tw-gap-[5px] sm:tw-gap-[7px] tw-bg-white tw-border tw-border-gray-300 tw-rounded-lg tw-text-black tw-min-w-[80px]"
                                                >
                                                    <FiFilter className="tw-text-[14px] sm:tw-text-[16px]" />
                                                    <span className="tw-hidden sm:tw-inline">Filters</span>
                                                    {areFiltersApplied() && (
                                                        <div className="tw-w-[16px] tw-h-[16px] sm:tw-w-[20px] sm:tw-h-[20px] tw-absolute -tw-left-1 sm:-tw-left-2 -tw-top-1 tw-bg-primary tw-rounded-full tw-flex tw-items-center tw-justify-center"></div>
                                                    )}
                                                </button>

                                                <div className="tw-relative inline-block text-left tw-min-w-[120px] sm:tw-min-w-auto">
                                                    <button
                                                        onClick={() => setIsOpen(!isOpen)}
                                                        className="tw-bg-white tw-border 2xl:tw-text-xl tw-text-[11px] sm:tw-text-[13px] tw-h-[36px] sm:tw-h-[40px] tw-text-nowrap tw-border-gray-300 tw-rounded-lg tw-text-black tw-flex tw-items-center tw-justify-center tw-gap-[5px] sm:tw-gap-[7px] tw-leading-none tw-px-[10px] sm:tw-px-[15px] tw-w-full"
                                                    >
                                                        <BiSort className="tw-text-[14px] sm:tw-text-[16px]" />
                                                        <span className="tw-truncate">Sort: {selectedOption.label}</span>
                                                        <FaChevronDown className="tw-text-xs tw-ml-1 sm:tw-ml-2 tw-flex-shrink-0" />
                                                    </button>

                                                    {isOpen && (
                                                        <div className="tw-absolute tw-mt-2 tw-z-[90] tw-w-full tw-min-w-[180px] tw-bg-white tw-border tw-border-gray-300 tw-rounded-lg tw-shadow-md tw-right-0">
                                                            {sortOptions.map((option) => (
                                                                <button
                                                                    key={option.value}
                                                                    onClick={() => {
                                                                        setSelectedOption(option);
                                                                        setCurrentPage(1);
                                                                        handleFilterChange({ value: option.value }, { name: "sort" });
                                                                        setIsOpen(false);
                                                                    }}
                                                                    className="tw-flex tw-gap-2 tw-w-full tw-py-2 tw-text-left tw-px-3 hover:tw-bg-gray-100 tw-text-[12px] sm:tw-text-[13px] tw-whitespace-nowrap"
                                                                >
                                                                    {option.label}
                                                                </button>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            <div className=" tw-mt-4 tw-mb-3  tw-w-full 2xl:tw-px-5">
                                                <p className="tw-text-[20px] tw-text-[#101828] tw-font-bold md:tw-font-medium md:tw-text-[19px] 2xl:tw-text-[25px]">
                                                    {resultSearchLocation}
                                                </p>
                                                <p className="resultsText 2xl:tw-text-[18px] ">{propertiesCount.toLocaleString()} Results</p>
                                            </div>
                                            <>
                                                {!loadProperties ? (
                                                    <div
                                                        className={`tw-grid tw-gap-[10px] tw-scrollbar-hide tw-place-items-center tw-grid-cols-1 sm:tw-grid-cols-2 lg:tw-grid-cols-2 xl:tw-grid-cols-2`}
                                                    >
                                                        {filteredProperties &&
                                                            filteredProperties.map((itm) => (
                                                                <PropertyComponent
                                                                    key={itm.ListingId}
                                                                    item={itm}
                                                                    setPreviewModal={setPreviewModal}
                                                                    isHomePage={true}
                                                                    renderImages={renderImages}
                                                                    handleFavorite={handleFavorite}
                                                                    properties={properties}
                                                                    // Mouse events handled by Leaflet map
                                                                    isModalOpen={isModalOpen || isDeleteModalOpen || !!previewModal}
                                                                />
                                                            ))}
                                                    </div>
                                                ) : (
                                                    <div className="tw-grid tw-gap-[10px] tw-scrollbar-hide tw-place-items-center tw-grid-cols-2 min-lg:tw-grid-cols-1 max-sm:tw-grid-cols-1 max-md:tw-grid-cols-2 lg:tw-grid-cols-2">
                                                        {[...Array(6)].map((_, index) => (
                                                            <PropertySkeleton key={index} />
                                                        ))}
                                                    </div>
                                                )}

                                                {/* InfoWindow functionality is now handled within the Leaflet map component */}

                                                <div className="pagination-controls tw-flex-nowrap max-sm:tw-pb-10 tw-mb-12 md:tw-mb-0   tw-flex tw-justify-center tw-mt-4">
                                                    {/* Handle pagination only if applicable */}
                                                    {filteredProperties.length > 0 && totalPages > 1 && (
                                                        <>
                                                            <Pagination
                                                                count={totalPages}
                                                                page={currentPage}
                                                                onChange={(_, data) => {
                                                                    if (propertyViewRef.current) {
                                                                        propertyViewRef.current.scrollIntoView({
                                                                            behavior: "smooth",
                                                                            block: "start",
                                                                        });
                                                                    }
                                                                    setLoadProperties(true); // Show loading during pagination
                                                                    setCurrentPage(data);
                                                                }}
                                                                size={window.innerWidth < 600 ? "small" : "medium"}
                                                                sx={{
                                                                    flexWrap: "nowrap",
                                                                    "& .Mui-selected": {
                                                                        backgroundColor: "var(--color-tertiary) !important", // Selected page background
                                                                        color: "white !important", // Selected page text color
                                                                        fontWeight: "bold",
                                                                        borderRadius: "8px",
                                                                    },
                                                                }}
                                                            />
                                                        </>
                                                    )}
                                                </div>
                                            </>
                                            {areFiltersApplied() && filteredProperties.length === 0 ? (
                                                <div className="tw-h-fulltw-flex tw-flex-col tw-items-center tw-justify-center">
                                                    <p>No properties found with the selected filters.</p>
                                                    <Button className="tw-bg-blue-500 tw-text-white tw-px-4 tw-py-2 tw-mt-4" onClick={clearFilters}>
                                                        Clear Filters
                                                    </Button>
                                                </div>
                                            ) : null}
                                            {!areFiltersApplied() && filteredProperties.length === 0 ? (
                                                <div className="tw-h-full tw-flex tw-flex-col tw-items-center tw-justify-center">
                                                    <p>No properties found.</p>
                                                </div>
                                            ) : null}

                                            {isModalOpen && (
                                                <FilterModal
                                                    isOpen={isModalOpen}
                                                    toggle={toggleModal}
                                                    getCornersProperties={getCornersProperties}
                                                    setCurrentPage={setCurrentPage}
                                                    filters={filters}
                                                    setFilters={(newFilters) => {
                                                        setCurrentPage(1);
                                                        setFilters(newFilters);
                                                    }}
                                                />
                                            )}
                                        </div>

                                        <div className="tw-bottom-[7rem] md:tw-bottom-2 tw-w-full tw-flex tw-justify-center md:tw-hidden tw-pb-2 tw-items-end tw-z-40 tw-absolute">
                                            <div
                                                className="tw-h-[40px] tw-w-[60px] tw-bg-[#171717] tw-text-white tw-rounded-[30px] tw-flex tw-items-center tw-justify-center tw-border-[0.5px] tw-border-white tw-shadow-lg"
                                                onClick={() => setToggle((pre) => !pre)}
                                                style={{ boxShadow: '0 3.53px 20px rgba(0, 0, 0, 0.37)' }}
                                            >
                                                <GrMap className="tw-text-[20px]" />
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {previewModal && (
                    <PreviewPage
                        navigate={navigate}
                        propertyId={previewModal?.ListingId}
                        isHomePage={true}
                        setPreviewModal={setPreviewModal}
                        handleFavorite={handleFavorite}
                        setIsFavoriteChanged={setIsFavoriteChanged}
                        isFavoriteChanged={isFavoriteChanged}
                        propertiesArray={properties}
                        isShareModal={isShareModal}
                        setIsShareModal={setIsShareModal}
                        isLogin={true}
                        favoriteProperties={favoriteProperties}
                    />
                )}
            </Container>
        </>
    );
};

export default Properties;
